import React, { useState } from 'react';
import {
    Typography,
    Stack,
    MenuItem,
    Select,
    InputLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    IconButton,
    TextField,
} from '@mui/material';
import DeleteIcon from "@mui/icons-material/Delete";

interface ScheduleDisplayProps {
    openModal: boolean;
    selectedBlock: any;
    handleCloseModal: () => void;
    handleSaveChanges: () => void;
    error: string | null;
    setError: React.Dispatch<React.SetStateAction<string | null>>;
    setSelectedBlock: React.Dispatch<any>;
    handleDeleteBlock: () => void;
}

const daysOfWeek = ['MON', 'TUES', 'WED', 'THURS', 'FRI', 'SAT', 'SUN']
const hours = Array.from({ length: 24 }, (_, i) => i)
const endHours = Array.from({ length: 24 }, (_, i) => (i + 1))

const formatHour = (hour: number) => {
    const formattedHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    const period = (hour < 12 || hour == 24) ? "AM" : "PM"
    return `${formattedHour} ${period}`
};

// E.164 Phone Number Validation Regex
const isValidPhoneNumber = (phone: string) => /^\+1\d{10}$/.test(phone)


const ScheduleEditBlockModal: React.FC<ScheduleDisplayProps> = ({
    openModal,
    selectedBlock,
    handleCloseModal,
    error,
    setError,
    setSelectedBlock,
    handleSaveChanges,
    handleDeleteBlock,
}) => {
    const [phoneErrors, setPhoneErrors] = useState<string[]>([])
    const [nameErrors, setNameErrors] = useState<string[]>([])
    const [confirmDelete, setConfirmDelete] = useState(false);


    const handleContactChange = (index: number, field: 'full_name' | 'phone_number', value: string) => {
        const updatedContacts = structuredClone(selectedBlock.contacts)

        if (field === 'phone_number') {
            // Ensure the phone number starts with +1 and only contains digits after that
            console.log(value)
            if (!value.startsWith("+1")) {
                value = "+1"
            } else {
                value = value.replace(/[^\d]/g, "") // Remove non-numeric characters
                value = "+1" + value.substring(1, 11) // Limit to 10 digits after +1
            }
        }

        updatedContacts[index][field] = value
        setSelectedBlock({
            ...selectedBlock,
            contacts: updatedContacts
        })

        if (field === 'full_name') {
            const updatedErrors = [...nameErrors]
            updatedErrors[index] = (value === '' || !value) ? "Must provide a contact name " : ""
            setNameErrors(updatedErrors)
        }

        console.log(value.length)
        // Validate phone number in real-time
        if (field === 'phone_number' && value.length == 12) {
            const updatedErrors = [...phoneErrors]
            updatedErrors[index] = isValidPhoneNumber(value) ? "" : "Must be in format +1XXXXXXXXXX (10 digits)"
            setPhoneErrors(updatedErrors)
        }
    };

    const validateContactsBeforeSave = () => {
        let errors: any[] = [];
        if (selectedBlock.contacts.length < 1) {
            setError("Must have at least 1 emergency contact");
            return false
        }
        for (let i = 0; i < selectedBlock.contacts.length; i++) {
            const contact = selectedBlock.contacts[i];
            errors.push(!isValidPhoneNumber(contact.phone_number) ? "Must be in format +1XXXXXXXXXX (10 digits)" : "")
        }

        setPhoneErrors(errors)


        if (errors.some((error: any) => error !== "")) {
            setError("Please correct invalid phone numbers before saving.")
            return false
        }

        errors = []
        for (let i = 0; i < selectedBlock.contacts.length; i++) {
            const contact = selectedBlock.contacts[i]
            const full_name = contact.full_name
            errors.push((full_name === '' || !full_name) ? "Must provide a contact name" : '')
        }
        setNameErrors(errors)

        if (errors.some((error: any) => error != "")) {
            setError("Please fill out the contacts")
            return false
        }

        setError(null)
        return true
    };

    const handleSaveWithValidation = () => {
        if (validateContactsBeforeSave()) {
            handleSaveChanges()
        }
    }

    const handleCloseModalHelper = () => {
        setConfirmDelete(false)
        handleCloseModal()
    }


    return (
        <Dialog open={openModal} onClose={handleCloseModalHelper} fullWidth={true}>
            <Stack direction='row' justifyContent="space-between" alignItems="center">
                <DialogTitle>Edit Schedule Block</DialogTitle>
                <IconButton
                    color="error"
                    onClick={() => setConfirmDelete(true)}
                    sx={{ marginRight: 3 }}
                >
                    <DeleteIcon fontSize='large' />
                </IconButton>
            </Stack>
            <DialogContent>
                <Stack spacing={2} justifyContent='top' >
                    <InputLabel id="day-select-label">Day of the Week</InputLabel>
                    <Select
                        labelId="day-select-label"
                        value={selectedBlock?.dayOfWeek || ''}
                        onChange={(e) => setSelectedBlock({ ...selectedBlock, dayOfWeek: e.target.value })}
                        fullWidth
                    >
                        {daysOfWeek.map((day, index) => (
                            <MenuItem key={index} value={index + 1}>{day}</MenuItem>
                        ))}
                    </Select>

                    <InputLabel id="start-hour-select-label">Start Time</InputLabel>
                    <Select
                        labelId="start-hour-select-label"
                        value={selectedBlock?.startHour.toString() || ''}
                        onChange={(e) => setSelectedBlock({ ...selectedBlock, startHour: parseInt(e.target.value) })}
                        fullWidth
                    >
                        {hours.map((hour) => (
                            <MenuItem key={hour} value={hour}>{formatHour(hour)}</MenuItem>
                        ))}
                    </Select>

                    <InputLabel id="end-hour-select-label">End Time</InputLabel>
                    <Select
                        labelId="end-hour-select-label"
                        value={selectedBlock?.endHour.toString() || ''}
                        onChange={(e) => setSelectedBlock({ ...selectedBlock, endHour: parseInt(e.target.value) })}
                        fullWidth
                    >
                        {endHours.map((hour) => (
                            <MenuItem key={hour} value={hour}>{formatHour(hour)}</MenuItem>
                        ))}
                    </Select>

                    <InputLabel id="number-of-hours">
                        {(selectedBlock?.endHour != undefined && selectedBlock?.startHour != undefined) ? `Time block set for ${selectedBlock.endHour - selectedBlock.startHour} hours` : ''}
                    </InputLabel>

                    {/* Contacts Section */}
                    <Typography variant="h6">Emergency Contacts</Typography>
                    {selectedBlock?.contacts?.map((contact: any, index: number) => (
                        <Stack key={index} direction="row" spacing={2} alignItems="center">
                            <TextField
                                label="Full Name"
                                value={contact.full_name}
                                onChange={(e) => handleContactChange(index, 'full_name', e.target.value)}
                                fullWidth
                                error={!!nameErrors[index]}
                                helperText={nameErrors[index]}
                            />
                            <TextField
                                label="Phone Number"
                                value={contact.phone_number}
                                onChange={(e) => handleContactChange(index, 'phone_number', e.target.value)}
                                fullWidth
                                error={!!phoneErrors[index]}
                                helperText={phoneErrors[index]}
                            />
                        </Stack>
                    ))}
                    <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogContent>
                            <Typography>Are you sure you want to delete this schedule block?</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                            <Button color="error" onClick={() => {
                                setConfirmDelete(false)
                                handleDeleteBlock()
                            }}>Delete</Button>
                        </DialogActions>
                    </Dialog>

                    {error && <Typography color="error">{error}</Typography>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCloseModal}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleSaveWithValidation}>Save</Button>
            </DialogActions>
        </Dialog >
    );
};

export default ScheduleEditBlockModal;
